import { InboundListItem } from '@/screens/order-inbounds/types';
import { MatchTerm } from '@/hooks/use-order-inbound-reducer';
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
  View,
} from 'react-native';
import { RecyclerListRef } from '@/components/recycler-list';
import { RefCallback, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLayoutContext } from '@/contexts/layout-context';
import { useOrderInboundDetailStore } from '@/screens/order-inbounds/store/order-inbound-detail-store';

interface UsePillNavProps {
  isInboundHistoryEnabled?: boolean;
  itemGap?: number;
  matchTerms: MatchTerm[];
}

export const usePillNav = ({
  isInboundHistoryEnabled,
  itemGap = 0,
  matchTerms,
}: UsePillNavProps) => {
  const { isSmallLayout } = useLayoutContext();

  const selectedInputUUID = useOrderInboundDetailStore(
    (state) => state.selectedInputUUID,
  );
  const setSelectedInputUUID = useOrderInboundDetailStore(
    (state) => state.actions.setSelectedInputUUID,
  );
  const itemToSwap = useOrderInboundDetailStore((state) => state.itemToSwap);

  // NOTE: There is something strange happening with the lifecycle of these
  // hook props; when they are accessed directly inside functions here,
  // they are always blank, and do not update once the prop value changes.
  const innerTerms = useRef<MatchTerm[]>([]);
  innerTerms.current = matchTerms;
  const innerSelectedInputUUID =
    useRef<typeof selectedInputUUID>(selectedInputUUID);
  innerSelectedInputUUID.current = selectedInputUUID;

  const scrollViewRef = useRef<ScrollView>(null);
  const itemRefs = useRef<Record<string, View>>({});
  const listRef = useRef<RecyclerListRef<InboundListItem>>(null);
  const matchTermIndex = useMemo(
    () =>
      matchTerms.reduce<Record<string, number>>((output, term, index) => {
        output[term.searchInputUUID] = index;
        return output;
      }, {}),
    [matchTerms],
  );

  const isScrollingToIndex = useRef(false);
  const isSelectingTermViaScroll = useRef(false);
  const scrollTimeout = useRef<ReturnType<typeof setTimeout>>(null);
  const scrollTargets = useRef<Record<string, [number, number]>>({});
  const prevY = useRef(0);
  const layoutHeightRef = useRef(0);

  const scrollToTerm = useCallback(
    (inputUUID: string) => {
      if (
        !listRef.current ||
        isSelectingTermViaScroll.current ||
        matchTermIndex[inputUUID] === undefined
      ) {
        return;
      }

      isScrollingToIndex.current = true;

      // Account for header items: 2 for small layouts (ORDER_OPTIONS + PILL_NAV), 1 for others (ListHeader)
      const indexOffset = isSmallLayout ? 2 : 1;

      listRef.current.scrollToIndex({
        animated: true,
        index: matchTermIndex[inputUUID] + indexOffset,
      });
    },
    [isSmallLayout, matchTermIndex],
  );

  useEffect(() => {
    const isInitialPosition =
      innerSelectedInputUUID.current &&
      matchTermIndex[innerSelectedInputUUID.current] === 0 &&
      prevY.current === 0;
    if (!innerSelectedInputUUID.current || isInitialPosition) {
      return;
    }

    // Only auto-scroll when the item is completely out of view; otherwise
    // hovering/selecting an item that's already visible shouldn't jump the list.
    const target = scrollTargets.current[innerSelectedInputUUID.current];
    const isCompletelyOutOfView =
      !target ||
      target[1] <= prevY.current ||
      target[0] >= prevY.current + layoutHeightRef.current;

    if (!isInboundHistoryEnabled || isCompletelyOutOfView) {
      scrollToTerm(innerSelectedInputUUID.current);
    }
    isSelectingTermViaScroll.current = false;
  }, [
    isInboundHistoryEnabled,
    matchTermIndex,
    selectedInputUUID,
    scrollToTerm,
  ]);

  const setSelectedTermViaScroll = useCallback(
    (inputUUID: string) => {
      // Prevents programmatic scrolls from the pill nav
      // from triggering term changes during animation or when swapping items.
      if (isScrollingToIndex.current || itemToSwap) {
        return;
      }
      isSelectingTermViaScroll.current = true;
      setSelectedInputUUID(inputUUID);
    },
    [setSelectedInputUUID, itemToSwap],
  );

  /** Track when the view stops scrolling */
  const handlePillNavContentScrollEnd = () => {
    isScrollingToIndex.current = false;
    isSelectingTermViaScroll.current = false;
  };

  /** On-scroll, change pill selection based on which list item is near the top of the view */
  const handlePillNavContentScroll = (
    event: NativeSyntheticEvent<NativeScrollEvent>,
  ) => {
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }
    scrollTimeout.current = setTimeout(() => {
      handlePillNavContentScrollEnd();
    }, 100);

    const contentHeight = event.nativeEvent.contentSize.height;
    const layoutHeight = event.nativeEvent.layoutMeasurement.height;
    const y = event.nativeEvent.contentOffset.y;
    layoutHeightRef.current = layoutHeight;

    if (prevY.current === y) {
      return;
    }
    const direction = prevY.current < y ? 'DOWN' : 'UP';
    prevY.current = y;

    // The last 20px are treated as the bottom
    const bottomY = contentHeight - layoutHeight - 20;
    const isAtBottom = y >= bottomY;

    const lastTerm = innerTerms.current[innerTerms.current.length - 1];
    if (isAtBottom && lastTerm) {
      setSelectedTermViaScroll(lastTerm.searchInputUUID);
      return;
    }

    // Size of buffer to give when scrolling manually,
    // before selection changes to the upcoming item,
    // depends on direction.
    const downBuffer = isSmallLayout ? 240 : 120;
    const buffer = direction === 'DOWN' ? downBuffer : 80;
    const targets = scrollTargets.current;
    for (const term of innerTerms.current) {
      if (!targets[term.searchInputUUID]) {
        continue;
      }
      const [start] = targets[term.searchInputUUID];
      if (y <= start + buffer) {
        setSelectedTermViaScroll(term.searchInputUUID);
        return;
      }
    }
  };

  /** Get a ref handler used to generate Y-coordinates for the list items */
  const getPillNavRefHandler =
    (item: InboundListItem): RefCallback<View> =>
    (element) => {
      const targets = scrollTargets.current;
      if (!item.metadata || !element) {
        return;
      }
      element.measure((_x, _y, _width, height) => {
        if (height === undefined) {
          return;
        }
        const searchInputUUID = item.metadata!.inputWithResult.uuid;
        const prevIndex = (matchTermIndex[searchInputUUID] ?? 0) - 1;
        const prevTerm = innerTerms.current[prevIndex];
        const prevItem = prevTerm ? targets[prevTerm.searchInputUUID] : null;
        const prevEndY = prevItem ? prevItem[1] : 0;
        const newEndY = prevEndY + height + itemGap;
        scrollTargets.current[searchInputUUID] = [prevEndY, newEndY];
      });
    };

  return {
    getPillNavRefHandler,
    handlePillNavContentScroll,
    handlePillNavContentScrollEnd,
    itemRefs,
    listRef,
    matchTermIndex,
    scrollViewRef,
  };
};
